import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Chip,
  keyframes,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LayoutContext } from "../../context/LayoutContext";

// Helper to format time relative to now
const getTimeAgo = (date) => {
  if (!date) return "";
  const now = new Date();
  const due = new Date(date);
  const diffMs = due - now;
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);

  if (diffMs < 0) {
    if (diffMins > -60) return `${Math.abs(diffMins)} mins overdue`;
    if (diffHours > -24) return `${Math.abs(diffHours)} hours overdue`;
    return `${Math.abs(diffDays)} days overdue`;
  }
  if (diffMins < 60) return `Due in ${diffMins} mins`;
  if (diffHours < 24) return `Due in ${diffHours} hours`;
  return `Due in ${diffDays} days`;
};

// Helper to format time
const formatTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Priority Item Component
const PriorityItem = ({ title, subtitle, tag, tagColor, bgColor, onClick }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        borderRadius: 2,
        backgroundColor: bgColor || alpha(tagColor, 0.08),
        "&:hover": {
          backgroundColor: bgColor ? alpha(bgColor, 0.9) : alpha(tagColor, 0.12),
          transform: "translateX(4px)",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: tagColor,
          letterSpacing: 0.3,
          mb: 0.5,
        }}
      >
        {tag}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "text.primary",
          mb: 0.25,
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          color: "text.secondary",
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  );
};

// Priority Card Component
const PriorityCard = ({
  icon: Icon,
  iconColor,
  title,
  items,
  borderColor,
  delay,
  collapsed,
  onViewAll,
  onItemClick,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: { xs: "100%", md: collapsed ? 200 : 260 },
        p: collapsed ? 2 : 2.5,
        borderRadius: "16px",
        border: "2px solid",
        borderColor: alpha(borderColor, 0.35),
        borderTop: "4px solid",
        borderTopColor: borderColor,
        backgroundColor: "#fff",
        animation: `${fadeInUp} 0.5s ease-out ${delay}s both`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 12px 24px ${alpha(borderColor, 0.15)}`,
        },
      }}
    >

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ fontSize: 18, color: iconColor }} />
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1E293B" }}>
            {title}
          </Typography>
        </Box>
        <Button
          onClick={onViewAll}
          endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontSize: 12,
            fontWeight: 500,
            color: "#3B82F6",
            p: 0,
            minWidth: "auto",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "transparent",
              color: "#2563EB",
              transform: "translateX(2px)",
            },
          }}
        >
          View All
        </Button>
      </Box>

      {/* Items */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {items.map((item, index) => (
          <PriorityItem
            key={`${item.id}-${index}`}
            title={item.title}
            subtitle={item.subtitle}
            tag={item.tag}
            tagColor={item.tagColor}
            bgColor={item.bgColor}
            onClick={() => onItemClick && onItemClick(item.id)}
          />
        ))}
      </Box>
    </Paper>
  );
};

// Main Component
const TodaysPriorityPanel = ({ leads = [] }) => {
  const { collapsed } = useContext(LayoutContext);
  const navigate = useNavigate();

  // Compute realtime data from leads
  const { urgentItems, pendingItems, alertItems } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000);

    // URGENT: Overdue followups & today's consultations
    const urgent = [];

    // Overdue followups
    leads.forEach((lead) => {
      if (!lead.next_followup) return;

      const followupDate = new Date(lead.next_followup);
      if (followupDate < now && lead.followup_status !== "completed") {
        urgent.push({
          id: lead._id,
          tag: "Overdue Follow-up",
          tagColor: "#EF4444",
          bgColor: "#FEF2F2",
          title: lead.client_name || lead.name || "Unknown",
          subtitle: getTimeAgo(lead.next_followup),
        });
      }
    });

    // Today's consultations (bookings today)
    leads.forEach((lead) => {
      if (!lead.bookings || lead.bookings.length === 0) return;

      lead.bookings.forEach((booking) => {
        if (booking.status === "Cancelled") return;

        const bookingDate = new Date(booking.date);
        const bookingDateStr = bookingDate.toDateString();
        const todayStr = today.toDateString();

        if (bookingDateStr === todayStr) {
          urgent.push({
            id: lead._id,
            tag: "Today's Consultation",
            tagColor: "#CA8A04",
            bgColor: "#FFFBEB",
            title: lead.client_name || lead.name || "Unknown",
            subtitle: `Starts at ${formatTime(booking.date)}`,
          });
        }
      });
    });

    // Sort urgent by time and limit to 3
    const sortedUrgent = urgent
      .sort((a, b) => {
        // Put overdue first
        if (a.tag === "Overdue Follow-up" && b.tag !== "Overdue Follow-up") return -1;
        if (a.tag !== "Overdue Follow-up" && b.tag === "Overdue Follow-up") return 1;
        return 0;
      })
      .slice(0, 3);

    // PENDING: Payment pending, contract waiting
    const pending = [];

    // Payment pending leads
    leads.forEach((lead) => {
      if (!lead.payments || lead.payments.length === 0) return;
      const hasPendingPayment = lead.payments.some(
        (p) => p.status === "pending" || p.status === "Partial"
      );
      if (hasPendingPayment) {
        pending.push({
          id: lead._id,
          tag: "Payment Pending",
          tagColor: "#F59E0B",
          bgColor: "#FFFBEB",
          title: lead.client_name || lead.name || "Unknown",
          subtitle: `${lead.payments.filter(p => p.status === "pending" || p.status === "Partial").length} pending payment(s)`,
        });
      }
    });

    // Leads waiting for contract/response
    leads.forEach((lead) => {
      const status = lead.current_status?.key?.toLowerCase() || "";
      if (["follow-up", "negotiation", "proposal-sent"].includes(status)) {
        pending.push({
          id: lead._id,
          tag: "Waiting Response",
          tagColor: "#6B7280",
          bgColor: "#F9FAFB",
          title: lead.client_name || lead.name || "Unknown",
          subtitle: `Stage: ${lead.current_status?.key || "Unknown"}`,
        });
      }
    });

    const sortedPending = pending.slice(0, 3);

    // ALERTS: Stuck leads (>3 days in same status), SLA breaches
    const alerts = [];

    // Stuck leads - no activity for 3+ days
    leads.forEach((lead) => {
      const lastUpdate = new Date(lead.updatedAt || lead.createdAt);
      if (lastUpdate < threeDaysAgo) {
        const daysSinceUpdate = Math.floor((today - lastUpdate) / (1000 * 60 * 60 * 24));
        alerts.push({
          id: lead._id,
          tag: `Stuck ${daysSinceUpdate} Days`,
          tagColor: "#F59E0B",
          bgColor: "#FFFBEB",
          title: lead.client_name || lead.name || "Unknown",
          subtitle: `Stage: ${lead.current_status?.key || "Unknown"}`,
        });
      }
    });

    // SLA breach warnings
    leads.forEach((lead) => {
      const createdDate = new Date(lead.createdAt);
      const daysSinceCreated = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      const status = lead.current_status?.key?.toLowerCase() || "";

      // If lead is still new/untouched after 2+ days
      if (daysSinceCreated >= 2 && ["new", "untouched", "inquiry"].includes(status)) {
        alerts.push({
          id: lead._id,
          tag: "SLA Warning",
          tagColor: "#EF4444",
          bgColor: "#FEF2F2",
          title: lead.client_name || lead.name || "Unknown",
          subtitle: `${daysSinceCreated} days without action`,
        });
      }
    });

    // Sort alerts by severity and limit to 3
    const sortedAlerts = alerts
      .sort((a, b) => {
        // Put SLA warnings first
        if (a.tag === "SLA Warning" && b.tag !== "SLA Warning") return -1;
        if (a.tag !== "SLA Warning" && b.tag === "SLA Warning") return 1;
        return 0;
      })
      .slice(0, 3);

    return {
      urgentItems: sortedUrgent,
      pendingItems: sortedPending,
      alertItems: sortedAlerts,
    };
  }, [leads]);

  // Handle item click - navigate to lead details
  const handleItemClick = (leadId) => {
    if (leadId) {
      navigate(`/leads/${leadId}`);
    }
  };

  return (
    <Box sx={{ mb: 3 }} px={2}>
      {/* Section Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 13,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Today's Priority Panel
        </Typography>

        <Chip
          icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
          label="AI-Driven Priorities"
          size="small"
          sx={{
            backgroundColor: alpha("#8B5CF6", 0.08),
            color: "#8B5CF6",
            fontWeight: 500,
            fontSize: 11,
            height: 26,
            borderRadius: 1.5,
            "& .MuiChip-icon": {
              color: "#8B5CF6",
            },
          }}
        />
      </Box>

      {/* Priority Cards */}
      <Box
        sx={{
          display: "flex",
          gap: collapsed ? 1.5 : 2,
          flexWrap: { xs: "wrap", md: collapsed ? "nowrap" : "wrap", lg: "nowrap" },
        }}
      >
        <PriorityCard
          icon={ErrorOutlineIcon}
          iconColor="#EF4444"
          title="Urgent"
          items={urgentItems}
          borderColor="#EF4444"
          delay={0}
          collapsed={collapsed}
          onViewAll={() => navigate("/dashboard/urgent-actions")}
          onItemClick={handleItemClick}
        />

        <PriorityCard
          icon={AccessTimeIcon}
          iconColor="#10B981"
          title="Pending"
          items={pendingItems}
          borderColor="#10B981"
          delay={0.1}
          collapsed={collapsed}
          onViewAll={() => navigate("/dashboard/pending-tasks")}
          onItemClick={handleItemClick}
        />

        <PriorityCard
          icon={WarningAmberIcon}
          iconColor="#F59E0B"
          title="Alerts"
          items={alertItems}
          borderColor="#F59E0B"
          delay={0.2}
          collapsed={collapsed}
          onViewAll={() => navigate("/dashboard/system-alerts")}
          onItemClick={handleItemClick}
        />
      </Box>
    </Box>
  );
};

export default TodaysPriorityPanel;
